import Vue from 'vue'
import Vuex from 'vuex'
import {
  getAuthInfo,
  getCartList, toggleCart as apiToggleCart,
  getCollectList, toggleCollect as apiToggleCollect,
  getCollectFolders as apiGetCollectFolders,
  createCollectFolder as apiCreateCollectFolder,
  updateCollectFolder as apiUpdateCollectFolder,
  deleteCollectFolder as apiDeleteCollectFolder,
} from '@/api'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    token: localStorage.getItem('token') || '',
    user: null,
    cartItems: [],
    collectIds: [],
    folders: [],
    collectDialog: { visible: false, mediaId: null },
    loginDialog: { visible: false, message: '' },
  },
  mutations: {
    SET_TOKEN(state, token) {
      state.token = token
      localStorage.setItem('token', token)
    },
    CLEAR_TOKEN(state) {
      state.token = ''
      localStorage.removeItem('token')
      state.user = null
    },
    SET_USER(state, user) {
      state.user = user
    },
    SET_CART(state, items) {
      state.cartItems = items
    },
    ADD_CART(state, item) {
      if (!state.cartItems.find(i => i.id === item.id && i.media_type === item.media_type)) {
        state.cartItems.push(item)
      }
    },
    REMOVE_CART(state, payload) {
      const id = typeof payload === 'number' ? payload : payload.id
      const media_type = typeof payload === 'number' ? undefined : payload.media_type
      const idx = state.cartItems.findIndex(i => i.id === id && (media_type ? i.media_type === media_type : true))
      if (idx >= 0) state.cartItems.splice(idx, 1)
    },
    CLEAR_CART(state) {
      state.cartItems = []
    },
    SET_COLLECT(state, list) {
      state.collectIds = list.map(c => c.media_id)
    },
    TOGGLE_COLLECT_ID(state, { mediaId, added }) {
      const idx = state.collectIds.indexOf(mediaId)
      if (added) {
        if (idx < 0) state.collectIds.push(mediaId)
      } else {
        if (idx >= 0) state.collectIds.splice(idx, 1)
      }
    },
    SET_FOLDERS(state, folders) {
      state.folders = folders
    },
    ADD_FOLDER(state, folder) {
      state.folders.push(folder)
    },
    UPDATE_FOLDER(state, { id, name }) {
      const f = state.folders.find(i => i.id === id)
      if (f) f.name = name
    },
    REMOVE_FOLDER(state, id) {
      const idx = state.folders.findIndex(i => i.id === id)
      if (idx >= 0) state.folders.splice(idx, 1)
    },
    SET_COLLECT_DIALOG(state, payload) {
      state.collectDialog = { ...state.collectDialog, ...payload }
    },
    SET_LOGIN_DIALOG(state, payload) {
      state.loginDialog = { ...state.loginDialog, ...payload }
    },
  },
  actions: {
    async fetchUser({ commit }) {
      try {
        const user = await getAuthInfo()
        commit('SET_USER', user)
      } catch (e) {
        console.error('获取用户信息失败', e)
        commit('CLEAR_TOKEN')
      }
    },
    async fetchCart({ commit, state }) {
      if (!state.token) return
      try {
        const items = await getCartList()
        const normalized = (items || []).map(item => {
          if (typeof item === 'number') return { id: item, name: '', price: 0 }
          return { id: item.id, name: item.name || '', price: Number(item.price) || 0 }
        })
        commit('SET_CART', normalized)
      } catch (e) {
        console.error('获取购物车失败', e)
      }
    },
    async toggleCart({ commit, state }, { mediaId, mediaItem }) {
      if (!state.token) {
        commit('SET_LOGIN_DIALOG', { visible: true, message: '请先登录' })
        return { added: false }
      }
      try {
        const mediaType = (mediaItem && mediaItem.media_type) || 'news'
        const res = await apiToggleCart({
          media_id: mediaId,
          media_type: mediaType,
          name: (mediaItem && mediaItem.name) || '',
          price: (mediaItem && mediaItem.price) || 0,
        })
        if (res.added) {
          if (mediaItem) commit('ADD_CART', { id: mediaId, ...mediaItem })
        } else {
          commit('REMOVE_CART', { id: mediaId, media_type: mediaType })
        }
        return res.added
      } catch (e) {
        console.error('操作购物车失败', e)
        throw e
      }
    },
    async fetchCollect({ commit, state }) {
      if (!state.token) return
      try {
        const list = await getCollectList()
        commit('SET_COLLECT', list)
      } catch (e) {
        console.error('获取收藏失败', e)
      }
    },
    async toggleCollect({ commit, state }, { mediaId, folderId }) {
      if (!state.token) {
        commit('SET_LOGIN_DIALOG', { visible: true, message: '请先登录' })
        return { collected: false }
      }
      try {
        const res = await apiToggleCollect({ media_id: mediaId, folder_id: folderId })
        commit('TOGGLE_COLLECT_ID', { mediaId, added: res.collected })
        return res.collected
      } catch (e) {
        console.error('操作收藏失败', e)
        throw e
      }
    },
    async fetchFolders({ commit, state }) {
      if (!state.token) return
      try {
        const folders = await apiGetCollectFolders()
        commit('SET_FOLDERS', folders)
      } catch (e) {
        console.error('获取收藏夹失败', e)
      }
    },
    async createFolder({ commit }, { name }) {
      const folder = await apiCreateCollectFolder({ name })
      commit('ADD_FOLDER', { id: folder.id, name: folder.name, count: 0 })
      return folder
    },
    async updateFolder({ commit }, { id, name }) {
      await apiUpdateCollectFolder({ id, name })
      commit('UPDATE_FOLDER', { id, name })
    },
    async deleteFolder({ commit }, { id }) {
      await apiDeleteCollectFolder({ id })
      commit('REMOVE_FOLDER', id)
    },
    showCollectDialog({ commit, state }, mediaId) {
      if (!state.token) {
        commit('SET_LOGIN_DIALOG', { visible: true, message: '请先登录' })
        return
      }
      commit('SET_COLLECT_DIALOG', { visible: true, mediaId })
    },
    hideCollectDialog({ commit }) {
      commit('SET_COLLECT_DIALOG', { visible: false, mediaId: null })
    },
  },
  getters: {
    isLoggedIn: state => !!state.token,
    cartItems: state => state.cartItems,
    cartCount: state => state.cartItems.length,
    cartTotal: state => state.cartItems.reduce((s, i) => s + (Number(i.price) || 0), 0),
    isInCart: state => id => state.cartItems.some(i => i.id === id),
    isCollected: state => id => state.collectIds.indexOf(id) >= 0,
    folders: state => state.folders,
    collectDialog: state => state.collectDialog,
    loginDialog: state => state.loginDialog,
  },
})
